'use client'

import classNames from 'classnames'
import React, { ForwardedRef, ReactElement, Ref, forwardRef } from 'react'
import { event as gaEvent } from 'nextjs-google-analytics'

import Box, { AsProp, BoxProps } from 'shared/components/Box/Box'

export type ButtonProps<As extends AsProp = 'button'> = BoxProps<As> & {
  size?: 'small' | 'medium' | 'large'
  color?: 'primary' | 'secondary'
  variant?: 'filled' | 'outlined' | 'link' | 'ghost' | 'unstyled'
  icon?: ReactElement
  position?: 'relative' | 'absolute'
  isLoading?: boolean
  display?: 'flex' | 'inlineFlex'
  background?: 'default' | 'inherit'
  eventName?: string
  eventParams?: Record<string, any>
  ref?: Ref<HTMLButtonElement>
}

export const BaseButton = <As extends AsProp = 'button'>(
  {
    children,
    className,
    size = 'large',
    color = 'primary',
    variant = 'filled',
    disabled,
    icon,
    position = 'relative',
    display = 'inlineFlex',
    background = 'default',
    eventName,
    eventParams,
    as,
    isLoading,
    onClick,
    ...props
  }: ButtonProps<As>,
  ref: ForwardedRef<HTMLElement>
) => {
  const AsPropValue = as ?? 'button'

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>
  ) => {
    if (eventName) gaEvent(eventName, eventParams)
    if (onClick) onClick(e)
  }

  const shouldApply = variant !== 'unstyled'

  return (
    <Box
      ref={ref}
      as={AsPropValue}
      type="button"
      className={classNames(
        shouldApply && 'button',
        shouldApply && `button ${size}`,
        shouldApply && `button ${color}`,
        shouldApply && `button ${variant}`,
        shouldApply && `button ${display}`,
        shouldApply && `button ${position}`,
        shouldApply && background === 'inherit' && 'button bgInherit',
        shouldApply && disabled && 'button disabled',
        className
      )}
      disabled={disabled}
      onClick={handleClick}
      {...props}
    >
      <span className="text">
        {!!icon && React.cloneElement(icon, { size: 20 })}
        {children}
      </span>
    </Box>
  )
}

export default forwardRef(BaseButton) as typeof BaseButton<AsProp>
